import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TenantProvider } from "@/context/TenantContext";
import { V360FeaturesProvider } from "@/hooks/useV360Features";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { PageSkeleton } from "@/components/common/Skeleton";
import { initSessionBridge } from "@/lib/sessionBridge";
import RequireSuperAdm from "@/components/auth/RequireSuperAdm";
import RequireAuth from "@/components/auth/RequireAuth";

// Eager (small / on critical path)
import Login from "@/pages/Login";
import AppLayout from "@/components/layout/AppLayout";

// Wrapper para lazy() que detecta "ChunkLoadError" (chunk obsoleto após deploy/hot-reload).
// O sintoma típico no overlay do CRA é: "Uncaught SyntaxError: Unexpected token '<'" — o servidor
// devolveu index.html para um chunk antigo que não existe mais, e o webpack tentou avaliá-lo como JS.
// Estratégia: tentar 1 vez recarregar a página; se ainda falhar, deixa o ErrorBoundary cuidar.
const RELOAD_FLAG = "e360.chunk-reloaded";
function lazyWithReload(loader) {
  return lazy(() =>
    loader().catch((err) => {
      const msg = String(err?.message || err);
      const isChunkErr =
        err?.name === "ChunkLoadError" ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /Unexpected token '?<'?/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg);
      if (isChunkErr) {
        try {
          if (!sessionStorage.getItem(RELOAD_FLAG)) {
            sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
            // Retorna uma promise pendente para evitar render do erro durante o reload
            return new Promise(() => {});
          }
        } catch { /* sessionStorage indisponível (private mode); cai pro throw */ }
      }
      throw err;
    })
  );
}
// Reseta a flag em navegações bem-sucedidas (após reload OK, primeiro paint limpa)
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
  });
}

// Public lead-magnet page (no auth)

// Lazy-loaded pages (code splitting for scalability)
const Dashboard           = lazyWithReload(() => import("@/pages/Dashboard"));
const Empresas            = lazyWithReload(() => import("@/pages/Empresas"));
const EmpresaDetail       = lazyWithReload(() => import("@/pages/EmpresaDetail"));
const S360Empresas        = lazyWithReload(() => import("@/pages/S360Empresas"));
const Projetos            = lazyWithReload(() => import("@/pages/Projetos"));
const ProjetoDetail       = lazyWithReload(() => import("@/pages/ProjetoDetail"));
const Recomendacoes       = lazyWithReload(() => import("@/pages/Recomendacoes"));
const RecomendacaoDetail  = lazyWithReload(() => import("@/pages/RecomendacaoDetail"));
const SprintBoard         = lazyWithReload(() => import("@/pages/SprintBoard"));
const Gantt               = lazyWithReload(() => import("@/pages/Gantt"));
const Calendario          = lazyWithReload(() => import("@/pages/Calendario"));
const Documentos          = lazyWithReload(() => import("@/pages/Documentos"));
const Reunioes            = lazyWithReload(() => import("@/pages/Reunioes"));
const PainelSemanal       = lazyWithReload(() => import("@/pages/PainelSemanal"));
const Usuarios            = lazyWithReload(() => import("@/pages/Usuarios"));
const Configuracoes       = lazyWithReload(() => import("@/pages/Configuracoes"));
const ConexaoRoutes       = lazyWithReload(() => import("@/pages/conexao/ConexaoRoutes"));
const Subscricoes         = lazyWithReload(() => import("@/pages/Subscricoes"));

// V360 module
// V360Dashboard (Painel V360 legado) removido — Phase 7.18: /app/v360 agora
// redireciona para /app/v360/empresa-dashboard.
const V360Wizard          = lazyWithReload(() => import("@/pages/v360/V360Wizard"));
const V360Menu            = lazyWithReload(() => import("@/pages/v360/V360Menu"));
const V360EmpresaResumo   = lazyWithReload(() => import("@/pages/v360/V360EmpresaResumo"));
const KPIsCustom          = lazyWithReload(() => import("@/pages/personalizacao/KPIsCustom"));
const VolumesCustom       = lazyWithReload(() => import("@/pages/personalizacao/VolumesCustom"));
const OKRsCustom          = lazyWithReload(() => import("@/pages/personalizacao/OKRsCustom"));
const V360EmpresaDashboard = lazyWithReload(() => import("@/pages/v360/V360EmpresaDashboard"));
const V360EmpresaEvolucao = lazyWithReload(() => import("@/pages/v360/V360EmpresaEvolucao"));
const V360Relatorios      = lazyWithReload(() => import("@/pages/v360/V360Relatorios"));
const V360RelatorioStandalone = lazyWithReload(() => import("@/pages/v360/V360RelatorioStandalone"));
const V360PlanoStandalone = lazyWithReload(() => import("@/pages/v360/V360PlanoStandalone"));
const AIAnalise           = lazyWithReload(() => import("@/pages/AIAnalise"));
const AcoesDecisoes       = lazyWithReload(() => import("@/pages/mgc/AcoesDecisoes"));
const AIAnaliseS360       = lazyWithReload(() => import("@/pages/AIAnaliseS360"));
const S360AcoesDecisoes   = lazyWithReload(() => import("@/pages/s360/mgc/S360AcoesDecisoes"));

// ADM Geral (Console Master)
const AdminLogin          = lazyWithReload(() => import("@/pages/admin/AdminLogin"));
const AdminLayout         = lazyWithReload(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard      = lazyWithReload(() => import("@/pages/admin/AdminDashboard"));
const AdminCrudRoute      = lazyWithReload(() => import("@/pages/admin/AdminCrudRoute"));
const AdminWorkspaces     = lazyWithReload(() => import("@/pages/admin/AdminWorkspaces"));
const AdminNovoCliente    = lazyWithReload(() => import("@/pages/admin/AdminNovoCliente"));
const AdminEditarCliente  = lazyWithReload(() => import("@/pages/admin/AdminEditarCliente"));
const AdminPlanos         = lazyWithReload(() => import("@/pages/admin/AdminPlanos"));
const AdminS360Planos     = lazyWithReload(() => import("@/pages/admin/AdminS360Planos"));
const AdminMGCParametros  = lazyWithReload(() => import("@/pages/admin/AdminMGCParametros"));
const AdminPlanoCustomParametros = lazyWithReload(() => import("@/pages/admin/AdminPlanoCustomParametros"));
const SetupSenha          = lazyWithReload(() => import("@/pages/SetupSenha"));
const TrocarSenha         = lazyWithReload(() => import("@/pages/TrocarSenha"));
const EsqueciSenha        = lazyWithReload(() => import("@/pages/EsqueciSenha"));
const WelcomeCliente      = lazyWithReload(() => import("@/pages/WelcomeCliente"));
const AdminValPersona     = lazyWithReload(() => import("@/pages/admin/AdminValPersona"));
const AdminValPrompts     = lazyWithReload(() => import("@/pages/admin/AdminValPrompts"));
const AdminValSanitizacaoLog = lazyWithReload(() => import("@/pages/admin/AdminValSanitizacaoLog"));
const AdminTraducoes      = lazyWithReload(() => import("@/pages/admin/AdminTraducoes"));
// Central de Referências (Phase 7.72.9 — UX mockado)
const AdminRefDashboard   = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasDashboard"));
const AdminSprintsPrompts = lazyWithReload(() => import("@/pages/admin/AdminSprintsPrompts"));
const Sprints360Projeto   = lazyWithReload(() => import("@/pages/sprints360/Sprints360Projeto"));
const Sprints360Atividades = lazyWithReload(() => import("@/pages/sprints360/Sprints360Atividades"));
const AdminRefProcesso    = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasProcesso"));
const AdminRefCategorias  = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasCategorias"));
const AdminRefFontes      = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasFontes"));
const AdminRefItens       = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasItens"));
const AdminRefEstrutural  = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasEstrutural"));
const AdminRefKpis        = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasKpis"));
const AdminRefEconomia    = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasEconomia"));
const AdminRefPaises      = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasPaises"));
const AdminRefLog         = lazyWithReload(() => import("@/pages/admin/referencias/AdminReferenciasLog"));
const AdminLLMModels      = lazyWithReload(() => import("@/pages/admin/AdminLLMModels"));
const AdminAIUsage        = lazyWithReload(() => import("@/pages/admin/AdminAIUsage"));
const AdminPaises         = lazyWithReload(() => import("@/pages/admin/AdminPaises"));
const AdminContatoEmails  = lazyWithReload(() => import("@/pages/admin/AdminContatoEmails"));
const AdminIdiomas        = lazyWithReload(() => import("@/pages/admin/AdminIdiomas"));
const AdminSetores        = lazyWithReload(() => import("@/pages/admin/AdminSetores"));
const AdminMatrizConexao  = lazyWithReload(() => import("@/pages/admin/AdminMatrizConexao"));
const AdminPortes         = lazyWithReload(() => import("@/pages/admin/AdminPortes"));
const AdminEstagios       = lazyWithReload(() => import("@/pages/admin/AdminEstagios"));
const AdminFrentes        = lazyWithReload(() => import("@/pages/admin/AdminFrentes"));
const AdminTopicos        = lazyWithReload(() => import("@/pages/admin/AdminTopicos"));
const AdminKPIs           = lazyWithReload(() => import("@/pages/admin/AdminKPIs"));
const AdminMatriz         = lazyWithReload(() => import("@/pages/admin/AdminMatriz"));
const AdminVolumetrias    = lazyWithReload(() => import("@/pages/admin/AdminVolumetrias"));
const AdminAuditoria      = lazyWithReload(() => import("@/pages/admin/AdminAuditoria"));

function Lazy({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/**
 * Redireciona URLs legadas /sprints360/:projetoId → /app/sprints360/:projetoId
 * (Phase 8.1 — rotas migradas para dentro do AppLayout).
 */
function Sprints360LegacyRedirect() {
  const path = window.location.pathname;
  const search = window.location.search || "";
  const match = path.match(/^\/sprints360\/(.+?)\/?$/);
  const projetoId = match ? match[1] : "";
  return <Navigate to={`/app/sprints360/${projetoId}${search}`} replace />;
}

function App() {
  // Bootstrap Supabase Auth session bridge — escuta SIGNED_IN/OUT/TOKEN_REFRESHED
  // e mantém `app_session` (LocalStorage) sincronizado para o TenantContext.
  useEffect(() => {
    initSessionBridge().catch((e) => console.warn("[App] sessionBridge init falhou:", e));
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <TenantProvider>
          <V360FeaturesProvider>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/setup-senha" element={<Lazy><SetupSenha /></Lazy>} />
            <Route path="/trocar-senha" element={<Lazy><TrocarSenha /></Lazy>} />
            <Route path="/esqueci-senha" element={<Lazy><EsqueciSenha /></Lazy>} />

            {/* App cliente (Valora360 / Sprints360) — Item A: nenhuma tela
                dentro do AppLayout monta sem sessão Supabase confirmada. */}
            <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route path="/app" element={<Navigate to="/app/welcome" replace />} />
              <Route path="/app/welcome"               element={<Lazy><WelcomeCliente /></Lazy>} />
              {/* Sprints360 */}
              <Route path="/app/dashboard"             element={<Lazy><Dashboard /></Lazy>} />
              <Route path="/app/empresas"              element={<Lazy><Empresas /></Lazy>} />
              <Route path="/app/empresas/:id"          element={<Lazy><EmpresaDetail /></Lazy>} />
              <Route path="/app/s360-empresas"         element={<Lazy><S360Empresas /></Lazy>} />
              <Route path="/app/projetos"              element={<Lazy><Projetos /></Lazy>} />
              <Route path="/app/projetos/:id"          element={<Lazy><ProjetoDetail /></Lazy>} />
              <Route path="/app/recomendacoes"         element={<Lazy><Recomendacoes /></Lazy>} />
              <Route path="/app/recomendacoes/:id"     element={<Lazy><RecomendacaoDetail /></Lazy>} />
              <Route path="/app/sprint-board"          element={<Lazy><SprintBoard /></Lazy>} />
              <Route path="/app/atividades"            element={<Lazy><Sprints360Atividades /></Lazy>} />
              <Route path="/app/gantt"                 element={<Lazy><Gantt /></Lazy>} />
              <Route path="/app/calendario"            element={<Lazy><Calendario /></Lazy>} />
              <Route path="/app/documentos"            element={<Lazy><Documentos /></Lazy>} />
              <Route path="/app/reunioes"              element={<Lazy><Reunioes /></Lazy>} />
              <Route path="/app/painel-semanal"        element={<Lazy><PainelSemanal /></Lazy>} />
              <Route path="/app/usuarios"              element={<Lazy><Usuarios /></Lazy>} />
              <Route path="/app/configuracoes"         element={<Lazy><Configuracoes /></Lazy>} />
              {/* Central de Conexão — submódulo de Configuração com rotas próprias (Fase 3 mockada) */}
              <Route path="/app/configuracoes/conexao/*" element={<Lazy><ConexaoRoutes /></Lazy>} />
              <Route path="/app/subscricoes"           element={<Lazy><Subscricoes /></Lazy>} />
              {/* Personalização — Phase 8.5 (feature-gated por hasCustomMetrics) */}
              <Route path="/app/personalizacao/kpis"    element={<Lazy><KPIsCustom /></Lazy>} />
              <Route path="/app/personalizacao/volumes" element={<Lazy><VolumesCustom /></Lazy>} />
              <Route path="/app/personalizacao/okrs"    element={<Lazy><OKRsCustom /></Lazy>} />
              <Route path="/app/analise-ia"             element={<Lazy><AIAnalise /></Lazy>} />
              <Route path="/app/acoes-decisoes"         element={<Lazy><AcoesDecisoes /></Lazy>} />
              <Route path="/app/analise-ia-s360"        element={<Lazy><AIAnaliseS360 /></Lazy>} />
              <Route path="/app/s360/acoes-decisoes"    element={<Lazy><S360AcoesDecisoes /></Lazy>} />
              {/* Sprints360 — Phase 8.1: rotas migradas para dentro do AppLayout
                  (single-tenant, chrome consistente com projeto legado). */}
              <Route path="/app/sprints360/:projetoId"  element={<Lazy><Sprints360Projeto /></Lazy>} />
              {/* Valora360 LITE — /app/v360 SEMPRE redireciona para o Painel V360
                  (empresa-dashboard), garantindo entrypoint consistente do app switch. */}
              <Route path="/app/v360"                  element={<Navigate to="/app/v360/empresa-dashboard" replace />} />
              <Route path="/app/v360/wizard"           element={<Lazy><V360Wizard /></Lazy>} />
              <Route path="/app/v360/menu"             element={<Lazy><V360Menu /></Lazy>} />
              <Route path="/app/v360/empresa-resumo"    element={<Lazy><V360EmpresaResumo /></Lazy>} />
              <Route path="/app/v360/empresa-dashboard" element={<Lazy><V360EmpresaDashboard /></Lazy>} />
              <Route path="/app/v360/relatorios"        element={<Lazy><V360Relatorios /></Lazy>} />
              <Route path="/app/v360/empresa-evolucao"  element={<Lazy><V360EmpresaEvolucao /></Lazy>} />
            </Route>

            {/* B7 (rodada de correção, Out/2026) — tela própria do relatório,
                em aba separada: exige login (RequireAuth) mas SEM AppLayout
                (sem menu lateral/sidebar). Acesso por empresa é checado pelo
                endpoint EMPRESA_SCOPED (403 tratado na própria página). */}
            <Route path="/app/v360/relatorio" element={<RequireAuth><Lazy><V360RelatorioStandalone /></Lazy></RequireAuth>} />
            <Route path="/app/v360/plano" element={<RequireAuth><Lazy><V360PlanoStandalone /></Lazy></RequireAuth>} />

            {/* ADM Geral (Master Console) — isolado visualmente */}
            <Route path="/admin/login" element={<Lazy><AdminLogin /></Lazy>} />
            <Route path="/admin" element={<RequireSuperAdm><Lazy><AdminLayout /></Lazy></RequireSuperAdm>}>
              <Route index                          element={<Lazy><AdminDashboard /></Lazy>} />
              {/* Workspaces & Acessos */}
              <Route path="workspaces"              element={<Lazy><AdminWorkspaces /></Lazy>} />
              <Route path="clientes/novo"           element={<Lazy><AdminNovoCliente /></Lazy>} />
              <Route path="clientes/editar/:workspaceId" element={<Lazy><AdminEditarCliente /></Lazy>} />
              <Route path="system-users"            element={<Lazy><AdminCrudRoute slug="system-users" /></Lazy>} />
              <Route path="papeis"                  element={<Lazy><AdminCrudRoute slug="papeis" /></Lazy>} />
              <Route path="auditoria"               element={<Lazy><AdminAuditoria /></Lazy>} />
              {/* Conteúdo Valora360 */}
              <Route path="frentes"                 element={<Lazy><AdminFrentes /></Lazy>} />
              <Route path="topicos"                 element={<Lazy><AdminTopicos /></Lazy>} />
              <Route path="kpis"                    element={<Lazy><AdminKPIs /></Lazy>} />
              <Route path="matriz"                  element={<Lazy><AdminMatriz /></Lazy>} />
              <Route path="volumetrias"             element={<Lazy><AdminVolumetrias /></Lazy>} />
              <Route path="benchmarks"              element={<Lazy><AdminCrudRoute slug="benchmarks" /></Lazy>} />
              <Route path="escala-cmm"              element={<Lazy><AdminCrudRoute slug="escala-cmm" /></Lazy>} />
              {/* Taxonomias */}
              <Route path="setores"                 element={<Lazy><AdminSetores /></Lazy>} />
              <Route path="matriz-conexao"          element={<Lazy><AdminMatrizConexao /></Lazy>} />
              <Route path="portes"                  element={<Lazy><AdminPortes /></Lazy>} />
              <Route path="estagios"                element={<Lazy><AdminEstagios /></Lazy>} />
              <Route path="paises"                  element={<Lazy><AdminPaises /></Lazy>} />
              <Route path="contato-emails"          element={<Lazy><AdminContatoEmails /></Lazy>} />
              <Route path="idiomas"                 element={<Lazy><AdminIdiomas /></Lazy>} />
              <Route path="traducoes"               element={<Lazy><AdminTraducoes /></Lazy>} />
              {/* Sprints360 */}
              <Route path="rituais"                 element={<Lazy><AdminCrudRoute slug="rituais" /></Lazy>} />
              <Route path="cat-doc"                 element={<Lazy><AdminCrudRoute slug="cat-doc" /></Lazy>} />
              <Route path="status-atv"              element={<Lazy><AdminCrudRoute slug="status-atv" /></Lazy>} />
              {/* IA & Prompts */}
              <Route path="val-persona"             element={<Lazy><AdminValPersona /></Lazy>} />
              <Route path="val-prompts"             element={<Lazy><AdminValPrompts /></Lazy>} />
              <Route path="val-sanitizacao-log"     element={<Lazy><AdminValSanitizacaoLog /></Lazy>} />
              <Route path="llm-models"              element={<Lazy><AdminLLMModels /></Lazy>} />
              <Route path="ai-usage"                element={<Lazy><AdminAIUsage /></Lazy>} />
              {/* Comercial */}
              <Route path="planos"                  element={<Lazy><AdminPlanos /></Lazy>} />
              <Route path="v360-planos"             element={<Lazy><AdminPlanos /></Lazy>} />
              <Route path="s360-planos"             element={<Lazy><AdminS360Planos /></Lazy>} />
              <Route path="faturamento"             element={<Lazy><AdminCrudRoute slug="faturamento" /></Lazy>} />
              {/* Sistema */}
              <Route path="parametros"              element={<Lazy><AdminCrudRoute slug="parametros" /></Lazy>} />
              <Route path="mgc-parametros"          element={<Lazy><AdminMGCParametros /></Lazy>} />
              <Route path="plano-customizado-parametros" element={<Lazy><AdminPlanoCustomParametros /></Lazy>} />
              <Route path="feature-flags"           element={<Lazy><AdminCrudRoute slug="feature-flags" /></Lazy>} />
              {/* Central de Referências — Phase 7.72.9 */}
              <Route path="referencias"             element={<Lazy><AdminRefDashboard /></Lazy>} />
              <Route path="referencias/processo"    element={<Lazy><AdminRefProcesso /></Lazy>} />
              <Route path="referencias/categorias"  element={<Lazy><AdminRefCategorias /></Lazy>} />
              <Route path="referencias/fontes"      element={<Lazy><AdminRefFontes /></Lazy>} />
              <Route path="referencias/itens"       element={<Lazy><AdminRefItens /></Lazy>} />
              <Route path="referencias/estrutural"  element={<Lazy><AdminRefEstrutural /></Lazy>} />
              <Route path="referencias/kpis"        element={<Lazy><AdminRefKpis /></Lazy>} />
              <Route path="referencias/economia"    element={<Lazy><AdminRefEconomia /></Lazy>} />
              <Route path="referencias/paises"      element={<Lazy><AdminRefPaises /></Lazy>} />
              <Route path="referencias/log"         element={<Lazy><AdminRefLog /></Lazy>} />
              <Route path="sprints-prompts"         element={<Lazy><AdminSprintsPrompts /></Lazy>} />
            </Route>

            {/* Sprints360 — rotas legadas standalone redirecionam para dentro
                do AppLayout (Phase 8.1 · single-tenant + chrome consistente). */}
            <Route path="/sprints360" element={<Navigate to="/app/projetos" replace />} />
            <Route path="/sprints360/:projetoId" element={<Sprints360LegacyRedirect />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </V360FeaturesProvider>
        </TenantProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
